import { FC } from 'react'
import { useNavigate } from 'react-router-dom'
import { Squares2X2Icon, ScaleIcon, MagnifyingGlassIcon, SwatchIcon, ArrowUpIcon } from '@heroicons/react/24/outline'
import { DataStatus } from 'models'
import theme from 'theme'
import OnyxiaTooltip from 'ui/components/tooltip/OnyxiaTooltip'
import { ActiveCPI } from '../../categoryDashboard.types'
import { ActiveCPICardWrapper } from './cpisSection.styles'
import { InfoIcon, LoadingSpinner } from '../icons'

interface Props {
  cpi: ActiveCPI
  benchmarkingLoading?: boolean
  recentFindingLoading?: boolean
}

// Percentile suffix follows English ordinal rules — special-casing the
// 11/12/13 cluster which always takes "th" regardless of last digit.
const ordinalSuffix = (n: number): string => {
  const tens = Math.abs(n) % 100
  if (tens >= 11 && tens <= 13) return 'th'
  switch (n % 10) {
    case 1:
      return 'st'
    case 2:
      return 'nd'
    case 3:
      return 'rd'
    default:
      return 'th'
  }
}

const formatPercentile = (value: number) => {
  const rounded = Math.round(value)
  return `${rounded}${ordinalSuffix(rounded)} percentile`
}

const ActiveCPICard: FC<Props> = ({ cpi, benchmarkingLoading, recentFindingLoading }) => {
  const navigate = useNavigate()
  const performanceClamped = Math.min(cpi.performance, 100)
  // Round before deciding arrow/color so a sub-0.5 change (e.g. 0.3) reads as
  // a flat 0% with no arrow rather than a rounded "0%" that still points up.
  const recentFindingChange = cpi.recentFindingChange != null ? Math.round(cpi.recentFindingChange) : null
  // Color reflects improvement, not raw sign: whether an increase is good
  // depends on the metric's health direction (asc = higher-is-better). The
  // arrow still shows the actual up/down movement, decoupled from the color.
  const isRecentFindingImprovement =
    recentFindingChange == null ||
    recentFindingChange === 0 ||
    (cpi.recentFindingDirection === 'asc' && recentFindingChange > 0) ||
    (cpi.recentFindingDirection === 'desc' && recentFindingChange < 0)
  const pendingLabel =
    cpi.status === DataStatus.Initializing
      ? 'Initializing'
      : cpi.status === DataStatus.Recalculating
      ? 'Recalculating'
      : null

  const handleNavigate = () => {
    navigate(`/library/cpis/${cpi.cpiId.toLowerCase()}/trends`)
  }

  return (
    <ActiveCPICardWrapper>
      <div className="card-header">
        <div className="cpi-chip" onClick={handleNavigate}>
          <span>{cpi.cpiId}</span>
        </div>
        <div className="cpi-name" onClick={handleNavigate}>
          {cpi.name}
        </div>
      </div>

      <div className="card-body">
        <div className="performance-section">
          <div className="performance-row">
            <div className="perf-left">
              <span className="perf-label">Performance score:</span>
              {pendingLabel ? (
                <span className="perf-value pending">
                  {pendingLabel}
                  <LoadingSpinner size={16} />
                </span>
              ) : (
                <span className="perf-value">{Math.round(cpi.performance)}%</span>
              )}
            </div>
            {!pendingLabel && (
              <div className="perf-right">
                <span className="month-label">last month:</span>
                {cpi.changeFromLastMonth == null ? (
                  <span className="month-label">---</span>
                ) : (
                  <div
                    className={`change-indicator ${cpi.changeFromLastMonth >= 0 ? 'positive' : 'negative'}`}
                  >
                    {cpi.changeFromLastMonth !== 0 && <ArrowUpIcon className="arrow-icon" />}
                    <span className="change-text">{Math.abs(cpi.changeFromLastMonth)}%</span>
                  </div>
                )}
              </div>
            )}
          </div>
          {!pendingLabel && (
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${(performanceClamped / 100) * 100}%` }} />
            </div>
          )}
        </div>

        <div className="metrics">
          <div className="metric-item">
            <div className="metric-icon-box">
              <Squares2X2Icon />
            </div>
            <div className="metric-content">
              <div className="metric-label">
                Weight
                <OnyxiaTooltip
                  title="The influence this individual CPI has on the category score."
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        color: `${theme.baseColors.greyscale[80]} !important`,
                        maxWidth: '272px !important',
                        width: '272px !important',
                        "html[data-theme='light'] &": {
                          color: `${theme.colors.menuItemText} !important`
                        }
                      }
                    }
                  }}
                >
                  <span className="info-icon-wrapper">
                    <InfoIcon />
                  </span>
                </OnyxiaTooltip>
              </div>
              <div className="metric-value">{pendingLabel ? <LoadingSpinner size={16} /> : cpi.weight}</div>
            </div>
          </div>

          <div className="metric-item">
            <div className="metric-icon-box">
              <ScaleIcon />
            </div>
            <div className="metric-content">
              <span className="metric-label">Benchmarking score</span>
              <span className="metric-value">
                {pendingLabel || benchmarkingLoading ? (
                  <LoadingSpinner size={16} />
                ) : cpi.benchmarkingScore == null ? (
                  'No benchmarking'
                ) : (
                  formatPercentile(cpi.benchmarkingScore)
                )}
              </span>
            </div>
          </div>

          <div className="metric-item">
            <div className="metric-icon-box">
              <MagnifyingGlassIcon />
            </div>
            <div className="metric-content">
              <span className="metric-label">Recent finding</span>
              <div className="recent-finding-row">
                <span className="metric-value">
                  {pendingLabel || recentFindingLoading ? <LoadingSpinner size={16} /> : cpi.recentFindingValue}
                </span>
                {!pendingLabel && !recentFindingLoading && (
                  <div className="recent-finding-change">
                    {recentFindingChange == null ? (
                      <span className="change-empty">---</span>
                    ) : (
                      <div className={`change-indicator ${isRecentFindingImprovement ? 'positive' : 'negative'}`}>
                        {recentFindingChange !== 0 && (
                          <ArrowUpIcon className={`arrow-icon ${recentFindingChange < 0 ? 'down' : ''}`} />
                        )}
                        <span className="change-text">{Math.abs(recentFindingChange)}</span>
                      </div>
                    )}
                    <span className="since-label">since last month</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="metric-item">
            <div className="metric-icon-box">
              <SwatchIcon />
            </div>
            <div className="metric-content">
              <span className="metric-label">Labels</span>
              <span className="metric-value">{pendingLabel ? <LoadingSpinner size={16} /> : cpi.labelsCount}</span>
            </div>
          </div>
        </div>
      </div>
    </ActiveCPICardWrapper>
  )
}

export default ActiveCPICard
